import { useEffect, useState } from "react";
import { Button, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { planColumns } from "./grid-columns";
import { Plan } from "../../../../models/plan";
import { planFormSchema } from "./plan-form.schema";
import { DeletePlanDialog } from "./delete-plan.dialog";
import { useDeleteData } from "../../../../hooks/useDeleteData";
import { UploadPlanDialog } from "./upload-plan/upload-plan.dialog";
import { planApiKeyStore } from "../../../../api/plan-api-key-store";
import { useFileDownload } from "../../../../components/file-download";
import { GuardedComponent } from "../../../../components/guarded-component";
import { UserPermissionName } from "../../../../models/user-permission-name";
import { SiTrackGrid } from "../../../../components/si-track-grid/si-track-grid";
import { planReviewApiKeyStore } from "../../../../api/plan-review-api-key-store";
import { GridColumnConfig, GridPreference } from "../../../../models/gridCoumnConfig";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { gridApiKeyStore } from "../../../../api/grid-api-key-store";
import { useSaveData } from "../../../../hooks/useSaveData";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../hooks/use-debounce";
import { useFormContext } from "react-hook-form";

interface CellProps {
    planData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface PlanDetailsComponent {
    title: string;
    planReviewTaskData: any;
    moduleCode: string;
    isLoading: boolean;
    setPlanIds: (planIds: any[]) => void;
    onSavePlan: (fileTypeData: any) => void;
}

export const PlanDetailsComponent = ({ title, planReviewTaskData, moduleCode, isLoading, onSavePlan, setPlanIds }: PlanDetailsComponent) => {

    const { setValue } = useFormContext();
    const [planID, setPlanID] = useState<any>();
    const [editItem, setEditItem] = useState<Partial<Plan>>();
    const [isConfirmDelete, setConfirmDialog] = useState<boolean>(false);

    // const { isLoading: isDeleteLoading, mutateAsync: mutateDeletePlanAsync } = useDeleteData(planApiKeyStore.deletePlan(planID),
    //     () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID));

    const { isLoading: isDeleteLoading, mutateAsync: mutateDeletePlanAsync } = useSaveData<any>(planApiKeyStore.removeMapPlan(),
        () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID));

    const { data: mapPlanData, isLoading: isMapPlanLoading, isFetching: isMapPlanFetching, refetch } = useLoadData<ApiResponse<any[]>>(
        planApiKeyStore.getPlans({
            // officeID: planRequestArgs.officeID,
            // projectID: planRequestArgs.projectID,
            unitID: planReviewTaskData?.unitID,
        }), !!planReviewTaskData?.unitID
    );

    const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

    const handleCreatePlan = () => {
        setEditItem({
            customerID: planReviewTaskData?.customerID,
            officeID: planReviewTaskData?.officeID,
            projectID: planReviewTaskData?.projectID,
            unitID: planReviewTaskData?.unitID
        });
    };

    const handleDownloadFile = async (data: any) => {
        const value = {
            planID: Number(data?.planID) || 0,
            fileName: data?.fileName,
            fileType: data?.fileType,
        };
        downloadFile({ additionalData: value });
    };

    const FileDownloadCell = ({ planData }: CellProps) => {
        const fileURL = planData?.fileURL;

        return (
            <td>
                {fileURL ? (
                    <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                        <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(planData)}>
                            <Tooltip title={<Typography variant="body1">Download</Typography>}>
                                <DownloadIcon className="btnEdit" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                ) : (<Typography variant="body1">No file uploaded</Typography>)
                }
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'fileName') {
            return (dataItem: any) => {
                return <FileDownloadCell planData={dataItem.dataItem} />;
            };
        }
    }

    const handleDeletePlan = async () => {
        const deletePlan = {
            planID: planID,
            unitID: planReviewTaskData?.unitID
        }
        await mutateDeletePlanAsync({ data: deletePlan });
        refetch();
        setConfirmDialog(false);
    };

    const handleEditPlan = (planData: any) => {
        setEditItem({
            ...planData,
            customerID: planReviewTaskData?.customerID,
            officeID: planReviewTaskData?.officeID,
            projectID: planReviewTaskData?.projectID,
            unitID: planReviewTaskData?.unitID,
        });
    }

    const openConfirmDialog = (id: any) => {
        setPlanID(id);
        setConfirmDialog(true);
    };

    const editAndDeleteElement = (planData: any) => {
        const itemType = planData?.dataItem?.itemType
        return (
            (itemType == "Plan") && (
                <>
                    <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_PLAN]}>
                        <IconButton aria-label="edit" onClick={() => handleEditPlan(planData?.dataItem)}>
                            <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                                <EditIcon className="btnEdit" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                    <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_PLAN]}>
                        <IconButton aria-label="delete" onClick={() => openConfirmDialog(planData?.dataItem?.planID)}>
                            <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                <DeleteIcon className="btnDelete" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                </>
            )
        );
    }


    // -------------------------------------------------Grid Layout------------------------------

    const titleName = title?.split(" ")?.join("");
    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout(`${titleName}PlanReviewLibrary`, `${titleName}PlanReviewLibrary`)
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredPlanLibraries, setFilteredPlanLibraries] = useState<Plan[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredPlanLibraryList = planReviewTaskData?.unitPlansList?.filter((PlanLibrary: any) =>
            Object.values(PlanLibrary).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredPlanLibraries(filteredPlanLibraryList || []);
    }, [debouncedSearchTerm, planReviewTaskData?.unitPlansList]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: `${titleName}PlanReviewLibrary`,
            pageName: `${titleName}PlanReviewLibrary`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: `${titleName}PlanReviewLibrary`,
            pageName: `${titleName}PlanReviewLibrary`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    const handleEditClose = (selectedPlans: any) => {
        const filePlans = planReviewTaskData?.unitPlansList?.filter((item: any) => item?.itemType?.toLowerCase() === "file") || [];
        const mergedPlans = [...selectedPlans, ...filePlans];
        const indexingPlan = mergedPlans.map((item, index) => ({ ...item, id: index + 1 }));

        const filteredPlanLibraryList = indexingPlan?.filter((PlanLibrary: any) =>
            Object.values(PlanLibrary).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );

        setFilteredPlanLibraries(filteredPlanLibraryList ?? []);

        // setPlanIds(indexingPlan?.map(item =>
        //     item.planType === "file"
        //         ? { fileID: item.fileID, itemType: item.itemType }
        //         : { planID: item.planID, itemType: item.itemType }
        // ));

        setPlanIds(indexingPlan?.map(plan => plan?.itemType?.toLowerCase() !== "file" && plan.planID));
        setEditItem(undefined);
    }

    const hanldeClosed = () => { setEditItem(undefined); }

    const handleSavePlan = (fileTypeData: any) => {
        if (fileTypeData?.planFileTypeID == 3 && moduleCode !== "PDB_PPI" && moduleCode !== "PDB_FILE") {
            onSavePlan(fileTypeData);
        }
        refetch();
        setEditItem(undefined);
    }

    return (
        <Grid container spacing={1}>
            <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                <Typography variant="h3" color="textPrimary">
                    PLANS
                </Typography>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='plan_review_plan'
                    columns={planColumns}
                    dataItemKey="id"
                    currentPageSize={5}
                    customCell={(field) =>
                        CustomGridCell({
                            field,
                        })
                    }
                    actionPermissions={{
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    CustomComponent={editAndDeleteElement}
                    isBusy={
                        isLoading ||
                        isFileDownloadLoading ||
                        isMapPlanLoading ||
                        isMapPlanFetching ||
                        isLoadingGridLayout
                    }

                    // -----------------Grid Layout-------
                    dataItems={filteredPlanLibraries || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                    handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
            </Grid>
            {(moduleCode == "PDB_ENERGY" || moduleCode == "PDB_PPI") && (
                <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Grid
                            container
                            direction="row"
                            sx={{
                                justifyContent: "flex-end",
                                alignItems: "flex-start",
                            }}
                        >
                            <Button variant="contained" style={{ width: '130px' }} onClick={handleCreatePlan}>
                                ADD PLANS
                            </Button>
                        </Grid>
                    </Grid>
                </GuardedComponent>
            )}
            {editItem && (
                <UploadPlanDialog
                    data={editItem}
                    config={{
                        saveConfig: editItem?.planID
                            ? planApiKeyStore.updatePlan()
                            : planApiKeyStore.createPlan(),
                        invalidateRoute: () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID),
                    }}
                    planData={planReviewTaskData?.unitPlansList ?? []}
                    mapPlanData={mapPlanData?.data ?? []}
                    yupSchema={planFormSchema}
                    onClosed={hanldeClosed}
                    onSavedPlan={handleSavePlan} />
            )}

            {isConfirmDelete && (
                <DeletePlanDialog
                    isParentLoading={isLoading}
                    isDeleteLoading={isDeleteLoading}
                    onDeletePlan={handleDeletePlan}
                    onClosed={() => setConfirmDialog(false)} />
            )}
        </Grid>
    );
}